import React, { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
// import { useDispatch } from 'react-redux'
import Header from '../../components/Header/index'
import LoadingAbsolute from '../../components/Loading/LoadingAbsolute'
import ProgressBar from '../TesislerHomePage/ProgressBar/ProgressBar'
import SelectTesis from './SelectTesis'
import './HobiBahcesiGirisSistemi.css'
import ProcessButtons from './ProcessButtons'
import NotificationsCollapse from './NotificationsCollapse'
import BarrierErrorModal from './BarrierErrorModal'
import BarrierQRModal from './BarrierQRModal'
import BarrierMethodModal from './BarrierMethodModal'
import PermissionWarningBanner from './PermissionWarningBanner'
import { AnimatePresence, motion } from 'framer-motion'
import axios from 'axios'
import { useDispatch } from 'react-redux'
import { setUser } from '../../store/userReducer'
import {
  buildBluetoothVerificationPayload,
  checkNativePermissions,
  requestBluetoothVerification,
  requestNativePermissionsWithResult,
  subscribePermissionUpdates,
} from './services/nativeBridge'
import { requestGeolocationVerification } from './services/geolocationVerification'
import { extractGardenFromApiResponse, mergeGardenSelection } from './services/gardenData'
import { persistSelectedGarden, resolveSelectedGarden } from './services/selectedGardenStorage'
import { getBarrierRequestErrorMessage, getBarrierSuccessMessage } from './services/barrierError'
import { runParallelVerification as coordinateParallelVerification } from './services/barrierVerificationCoordinator'
import {
  areMandatoryPermissionsGranted,
  buildBarrierVerificationPayload,
  buildPermissionDetails,
  buildPermissionDetailsFromNative,
  isBarrierProximityVerified,
  resolvePermissionState,
  resolvePermissionStateFromNative,
} from './services/permissionGate'

const API_BASE_URL = process.env.REACT_APP_BASE_URL || 'https://app.yanibasimdakaratay.com'
const API_TIMEOUT_MS = 10000  // API cagrilarinin sonsuz bekleme degil 10s'de timeout olmasi icin

const getNumericTesisValue = (...values) => {
  const matchedValue = values.find((value) => value !== undefined && value !== null && value !== '')
  const parsedValue = Number(matchedValue)

  return Number.isFinite(parsedValue) ? parsedValue : null
}

const HobiBahcesiGirisSistemi = () => {
  let location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const routeSelectedTesis = resolveSelectedGarden(location?.state?.selectedTesis)
  const token = location.search.slice(7)
  // `loading`: ilk data fetch; `isBarrierActionInProgress`: bariyer dogrulama (tam sayfa overlay).
  // Barrier action + permission request kendi state'lerini kullanir (UI bloklamaz).
  const [loading, setLoading] = useState(true)
  const [selectedTesis, setSelectedTesis] = useState(() => resolveSelectedGarden(location?.state?.selectedTesis))
  const [barrierErrorModal, setBarrierErrorModal] = useState(false)
  const [error, setError] = useState(null)
  const [openQRModal, setOpenQRModal] = useState(false)
  const [dolulukOrani, setDolulukOrani] = useState('0')
  const [notifications, setNotifications] = useState([])
  const [barrierSuccessMessage, setBarrierSuccessMessage] = useState(null)
  const [permissionState, setPermissionState] = useState({
    bluetoothGranted: false,
    geolocationGranted: false,
  })
  const [isPermissionRequestInProgress, setIsPermissionRequestInProgress] = useState(false)
  const [isBarrierActionInProgress, setIsBarrierActionInProgress] = useState(false)
  // Banner icin: detayli izin bilgileri + son kontrol zamani.
  // Ilk yuklemede aktif edilir, window focus'ta yenilenir.
  const [permissionDetails, setPermissionDetails] = useState([])
  const [permissionsCheckedAt, setPermissionsCheckedAt] = useState(null)
  const [hasInitialPermissionCheck, setHasInitialPermissionCheck] = useState(false)

  // QR + yön/yöntem aşamalı akış state'leri
  const [selectedDirection, setSelectedDirection] = useState(null)        // 'entry' | 'exit'
  const [barrierActionDirection, setBarrierActionDirection] = useState(null) // hangi buton işlemde
  const [methodModalOpen, setMethodModalOpen] = useState(false)
  const [qrPayload, setQrPayload] = useState('')
  const [qrExpiresIn, setQrExpiresIn] = useState(15)

  const barrierLocation = {
    latitude: getNumericTesisValue(selectedTesis?.barrier_lat, selectedTesis?.location_lat),
    longitude: getNumericTesisValue(selectedTesis?.barrier_lng, selectedTesis?.location_long),
  }
  const barrierDistanceThreshold = getNumericTesisValue(selectedTesis?.geo_threshold_m) || 50

  const getData = async () => {
    setLoading(true)
    const initialGarden = resolveSelectedGarden(location?.state?.selectedTesis)

    try {
      const response = await axios.get(`${API_BASE_URL}/api/hobi-bahce`, {
        headers: {
          Authorization: JSON.parse(localStorage.getItem('token')),
        },
        timeout: API_TIMEOUT_MS,
      })

      const garden = extractGardenFromApiResponse(response.data)
      const nextSelectedTesis = mergeGardenSelection(initialGarden, garden)

      if (nextSelectedTesis) {
        persistSelectedGarden(nextSelectedTesis)
        dispatch(setUser(nextSelectedTesis))
        setSelectedTesis(nextSelectedTesis)
        setDolulukOrani(nextSelectedTesis.doluluk_orani ?? garden?.doluluk_orani ?? '0')
        setNotifications(nextSelectedTesis.duyurular || garden?.duyurular || [])
      } else if (initialGarden) {
        dispatch(setUser(initialGarden))
        setSelectedTesis(initialGarden)
      }
    } catch (requestError) {
      if (initialGarden) {
        dispatch(setUser(initialGarden))
        setSelectedTesis(initialGarden)
        setLoading(false)
        return
      }

      const isTimeout = requestError?.code === 'ECONNABORTED'
      setError(isTimeout ? 'Bağlantı zaman aşımı, lütfen tekrar deneyin.' : 'API hatası oluştu')
      setBarrierErrorModal(true)
    } finally {
      setLoading(false)
    }
  }

  const openBarrierRequest = async (verificationState, direction = null) => {
    const payload = buildBarrierVerificationPayload(
      verificationState.bluetoothResult,
      verificationState.geolocationResult
    )
    // Kullanıcı yön seçtiyse backend'e bildir (precheck'te onaylanan yön)
    if (direction) {
      payload.barrier_type = direction
    }
    const response = await axios.post(
      `${API_BASE_URL}/api/hobi-bahce/open-barrier`,
      payload,
      {
        headers: {
          Authorization: JSON.parse(localStorage.getItem('token')),
        },
        timeout: API_TIMEOUT_MS,
      }
    )

    return response.data
  }

  useEffect(() => {
    getData()
    /* eslint-disable */
  }, [])

  // react-bootstrap çoklu modal geçişlerinde (QR fullscreen → success modal)
  // body scroll-lock artığı takılı kalabiliyor. Tüm modallar kapalıysa temizle.
  useEffect(() => {
    const anyModalOpen = barrierErrorModal || methodModalOpen || openQRModal
    if (!anyModalOpen) {
      document.body.style.overflow = ''
      document.body.style.paddingRight = ''
      document.body.classList.remove('modal-open')
    }
  }, [barrierErrorModal, methodModalOpen, openQRModal])

  useEffect(() => {
    if (token) {
      localStorage.setItem('token', JSON.stringify(token))
    }

    if (!routeSelectedTesis) {
      return
    }

    setLoading(false)
    setSelectedTesis((currentSelectedTesis) => {
      const mergedGarden = mergeGardenSelection(routeSelectedTesis, currentSelectedTesis)
      if (mergedGarden) {
        persistSelectedGarden(mergedGarden)
        dispatch(setUser(mergedGarden))
      }
      return mergedGarden
    })
  }, [token, routeSelectedTesis, dispatch])

  // Ilk garden yuklendikten sonra otomatik izin kontrolu (kullanici butona basmadan).
  useEffect(() => {
    if (!selectedTesis || hasInitialPermissionCheck) {
      return
    }
    setHasInitialPermissionCheck(true)
    // Light check — bariyer acmaz, sadece izin durumu sorgular.
    requestMandatoryPermissions()
  }, [selectedTesis, hasInitialPermissionCheck])

  // Kullanici telefon ayarlarindan donunce auto-retry: window focus'ta tekrar kontrol et.
  useEffect(() => {
    const handleFocus = () => {
      if (!selectedTesis || isPermissionRequestInProgress || isBarrierActionInProgress) {
        return
      }
      if (permissionsCheckedAt && Date.now() - permissionsCheckedAt < 3000) {
        return // Cok sik retry'i engelle
      }
      requestMandatoryPermissions()
    }
    window.addEventListener('focus', handleFocus)
    document.addEventListener('visibilitychange', handleFocus)
    return () => {
      window.removeEventListener('focus', handleFocus)
      document.removeEventListener('visibilitychange', handleFocus)
    }
  }, [selectedTesis, isPermissionRequestInProgress, isBarrierActionInProgress, permissionsCheckedAt])

  const toggleBarrierError = () => {
    setBarrierErrorModal(!barrierErrorModal)
  }

  const showBarrierError = (errorType) => {
    setBarrierSuccessMessage(null)
    setError(errorType)
    setBarrierErrorModal(true)
  }

  const runParallelBarrierVerification = async () => {
    const bluetoothPromise = requestBluetoothVerification(buildBluetoothVerificationPayload(selectedTesis))
    const geolocationPromise = requestGeolocationVerification(barrierLocation, barrierDistanceThreshold)

    return coordinateParallelVerification(bluetoothPromise, geolocationPromise)
  }

  useEffect(() => {
    const unsubscribe = subscribePermissionUpdates((nativePermissions) => {
      applyNativePermissionResult(nativePermissions)
    })

    return unsubscribe
  }, [])

  const applyNativePermissionResult = (nativePermissions) => {
    const nextPermissionState = resolvePermissionStateFromNative(nativePermissions)
    setPermissionState(nextPermissionState)
    setPermissionDetails(buildPermissionDetailsFromNative(nativePermissions))
    setPermissionsCheckedAt(Date.now())
  }

  const applyVerificationPermissionResult = (verificationState) => {
    const nextPermissionState = resolvePermissionState(verificationState.bluetoothResult, verificationState.geolocationResult)
    setPermissionState(nextPermissionState)
    setPermissionDetails(buildPermissionDetails(verificationState.bluetoothResult, verificationState.geolocationResult))
    setPermissionsCheckedAt(Date.now())
    return nextPermissionState
  }

  const checkMandatoryPermissions = async () => {
    const nativePermissions = await checkNativePermissions(['bluetooth', 'geolocation'])

    if (nativePermissions) {
      applyNativePermissionResult(nativePermissions)
      return resolvePermissionStateFromNative(nativePermissions)
    }

    const verificationState = await runParallelBarrierVerification()
    return applyVerificationPermissionResult(verificationState)
  }

  const requestMandatoryPermissions = async () => {
    if (isPermissionRequestInProgress || isBarrierActionInProgress || loading) {
      return
    }

    setIsPermissionRequestInProgress(true)

    try {
      await checkMandatoryPermissions()
    } catch (error) {
      showBarrierError('Bluetooth')
    } finally {
      setIsPermissionRequestInProgress(false)
    }
  }

  // Banner'daki "Izin Ver" butonu handler'i.
  // RN tarafina REQUEST_PERMISSIONS gonderir ve PERMISSIONS_RESULT cevabini bekler.
  const grantPermissions = async () => {
    if (isPermissionRequestInProgress || isBarrierActionInProgress || loading) {
      return
    }
    setIsPermissionRequestInProgress(true)
    try {
      const nativePermissions = await requestNativePermissionsWithResult(['bluetooth', 'geolocation'])

      if (nativePermissions) {
        applyNativePermissionResult(nativePermissions)
        return
      }

      await checkMandatoryPermissions()
    } catch (error) {
      showBarrierError('Bluetooth')
    } finally {
      setIsPermissionRequestInProgress(false)
    }
  }

  // Aşama 1: Giriş/Çıkış buton → backend precheck (içerde mi? bu yön açılabilir mi?)
  const handleDirectionSelected = async (direction) => {
    if (loading || isPermissionRequestInProgress || isBarrierActionInProgress) {
      return
    }
    setBarrierActionDirection(direction)
    setIsBarrierActionInProgress(true)
    try {
      const response = await axios.post(
        `${API_BASE_URL}/api/hobi-bahce/barrier-precheck`,
        { barrier_type: direction },
        {
          headers: { Authorization: JSON.parse(localStorage.getItem('token')) },
          timeout: API_TIMEOUT_MS,
        }
      )
      const data = response.data?.data || response.data || {}
      if (!data.can_proceed) {
        showBarrierError(data.message || 'Bu işlem şu an yapılamaz.')
        return
      }
      // precheck geçti — yöntem seçim modalını aç
      setSelectedDirection(direction)
      setMethodModalOpen(true)
    } catch (requestError) {
      showBarrierError(
        getBarrierRequestErrorMessage(requestError) ||
          (requestError?.response ? 'API hatası oluştu' : 'Bağlantı hatası')
      )
    } finally {
      setIsBarrierActionInProgress(false)
      setBarrierActionDirection(null)
    }
  }

  // Aşama 2a: Temassız Giriş (BLE/GPS) — mevcut yakınlık doğrulama akışı
  const handleContactlessMethod = async () => {
    setMethodModalOpen(false)
    if (!selectedDirection) {
      return
    }
    setIsBarrierActionInProgress(true)
    try {
      const verificationState = await runParallelBarrierVerification()

      const nativePermissions = await checkNativePermissions(['bluetooth', 'geolocation'])
      if (nativePermissions) {
        applyNativePermissionResult(nativePermissions)
      } else {
        applyVerificationPermissionResult(verificationState)
      }

      if (!isBarrierProximityVerified(verificationState.bluetoothResult, verificationState.geolocationResult)) {
        const btResult = verificationState.bluetoothResult || {}
        const geoResult = verificationState.geolocationResult || {}
        const btReason = !btResult.ok
          ? `BT:${btResult.action || 'NO_RESPONSE'}${btResult.payload?.reason ? '/' + btResult.payload.reason : ''}`
          : 'BT:OK'
        const geoReason = !geoResult.ok
          ? `GPS:${geoResult.reason || 'FAIL'}${geoResult.distance != null ? '/' + geoResult.distance + 'm' : ''}`
          : 'GPS:OK'
        showBarrierError(`Yakınlık doğrulanamadı (${btReason}, ${geoReason}). Bariyere yaklaşın veya QR ile deneyin.`)
        return
      }

      const responseData = await openBarrierRequest(verificationState, selectedDirection)
      setBarrierSuccessMessage(getBarrierSuccessMessage(responseData))
      setError('')
      setBarrierErrorModal(true)
    } catch (requestError) {
      showBarrierError(getBarrierRequestErrorMessage(requestError) || (requestError?.response ? 'API hatası oluştu' : 'Bluetooth'))
    } finally {
      setIsBarrierActionInProgress(false)
    }
  }

  // Aşama 2b: QR ile Giriş — backend token üretir, QR modal açılır
  const requestQrToken = async () => {
    const response = await axios.post(
      `${API_BASE_URL}/api/hobi-bahce/qr-token`,
      { barrier_type: selectedDirection },
      {
        headers: { Authorization: JSON.parse(localStorage.getItem('token')) },
        timeout: API_TIMEOUT_MS,
      }
    )
    return response.data?.data || response.data || {}
  }

  const handleQrMethod = async () => {
    setMethodModalOpen(false)
    if (!selectedDirection) {
      return
    }
    try {
      const data = await requestQrToken()
      setQrPayload(data.qr_payload || '')
      setQrExpiresIn(data.expires_in || 15)
      setOpenQRModal(true)
    } catch (requestError) {
      showBarrierError(getBarrierRequestErrorMessage(requestError) || 'QR kod alınamadı.')
    }
  }

  // QR süresi dolunca "Yenile" — yeni token iste
  const handleQrRefresh = async () => {
    try {
      const data = await requestQrToken()
      setQrPayload(data.qr_payload || '')
      setQrExpiresIn(data.expires_in || 15)
    } catch (requestError) {
      setOpenQRModal(false)
      showBarrierError(getBarrierRequestErrorMessage(requestError) || 'QR kod yenilenemedi.')
    }
  }

  // QR okutulup bariyer açıldı — QR modal kapat, başarı mesajı göster
  const handleQrSuccess = () => {
    setOpenQRModal(false)
    setQrPayload('')
    setBarrierSuccessMessage('Bariyer açıldı. Geçiş yapabilirsiniz.')
    setError('')
    setBarrierErrorModal(true)
  }

  const guestOnClick = () => {
    if (selectedTesis) {
      persistSelectedGarden(selectedTesis)
    }

    navigate('/hobiGuestProcess', {
      state: {
        selectedTesis: selectedTesis,
      },
    })
  }

  const historyOnClick = () => {
    if (selectedTesis) {
      persistSelectedGarden(selectedTesis)
    }

    navigate('/hobiHistoryPage', {
      state: {
        selectedTesis: selectedTesis,
      },
    })
  }

  const hasMissingPermissions = !areMandatoryPermissionsGranted(permissionState) && permissionDetails.length > 0

  return (
    <>
      {loading ? <LoadingAbsolute formText={'Belgeleriniz yükleniyor,'} text={'Lütfen bekleyiniz...'} formLodading={true} /> : null}
      {isBarrierActionInProgress ? <LoadingAbsolute text={'Doğrulanıyor, lütfen bekleyiniz...'} /> : null}
      <div className='hide-scrollbar'>
        <div
          style={{
            backgroundColor: '#fff',
            position: 'sticky',
            top: 0,
            zIndex: 999,
          }}
        >
          <Header onClick={() => navigate('/')} rules={selectedTesis?.id === 2} />
        </div>
        <AnimatePresence initial={false} mode='wait'>
          {hasMissingPermissions ? (
            <motion.div
              key='permission-banner'
              className='container'
              initial={{ opacity: 0, y: -8, height: 0 }}
              animate={{ opacity: 1, y: 0, height: 'auto' }}
              exit={{ opacity: 0, y: -8, height: 0 }}
              transition={{ duration: 0.25, ease: 'easeOut' }}
              style={{ overflow: 'hidden' }}
            >
              <PermissionWarningBanner
                permissionDetails={permissionDetails}
                onGrantPermissions={grantPermissions}
                isRequesting={isPermissionRequestInProgress}
                lastCheckedAt={permissionsCheckedAt}
              />
            </motion.div>
          ) : null}
        </AnimatePresence>
        <div className='container'>
          <SelectTesis data={selectedTesis} selectedItem={selectedTesis} />
          <ProcessButtons
            entryBarrierOnClick={() => handleDirectionSelected('entry')}
            exitBarrierOnClick={() => handleDirectionSelected('exit')}
            guestOnClick={guestOnClick}
            historyOnClick={historyOnClick}
            barrierActionDirection={barrierActionDirection}
            isBarrierActionInProgress={isBarrierActionInProgress}
          />
          {dolulukOrani >= 0 ? <ProgressBar doluluk={dolulukOrani} /> : null}
          <NotificationsCollapse selectedTesis={selectedTesis} notifications={notifications} />
        </div>
      </div>
      <BarrierMethodModal
        open={methodModalOpen}
        direction={selectedDirection}
        onClose={() => setMethodModalOpen(false)}
        onContactless={handleContactlessMethod}
        onQr={handleQrMethod}
        isBusy={isBarrierActionInProgress}
      />
      <BarrierQRModal
        open={openQRModal}
        qrPayload={qrPayload}
        expiresIn={qrExpiresIn}
        direction={selectedDirection}
        onRefresh={handleQrRefresh}
        onSuccess={handleQrSuccess}
        onClose={() => setOpenQRModal(false)}
      />
      <BarrierErrorModal error={error} modal={barrierErrorModal} toggle={toggleBarrierError} successMessage={barrierSuccessMessage} />
    </>
  )
}

export default HobiBahcesiGirisSistemi
